import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import Keycloak from 'keycloak-js';

const PrincipalContext = createContext();

const ORG_STORAGE_KEY = 'currOrg';

export const PrincipalProvider = ({ children }) => {
  const keycloakRef = useRef();
  const [principal, setPrincipal] = useState();
  const [org, setOrg] = useState();
  const [roles, setRoles] = useState();

  useEffect(() => {
    // Create the instance only if it doesn't already exist.
    const keycloakExists = !!keycloakRef.current;
    if (!keycloakExists) {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = protocol.includes('https') ? '9443' : '9999';
      const authServerUrl = `${protocol}//${hostname}:${port}/auth`;
      keycloakRef.current = new Keycloak({
        url: authServerUrl,
        realm: 'uaa',
        clientId: 'frontend'
      });
    }
    const keycloakInstance = keycloakRef.current;

    if (keycloakExists) {
      return;
    }

    keycloakInstance
      .init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        pkceMethod: 'S256',
        scope: 'openid profile email roles'
      })
      .then(authenticated => {
        if (!authenticated) {
          keycloakInstance.login();
          return;
        }
        const tokenParsed = keycloakInstance.tokenParsed;
        const principalData = {
          username: tokenParsed.preferred_username || tokenParsed.username,
          name: tokenParsed.name || tokenParsed.preferred_username,
          orgs: tokenParsed.orgs ? tokenParsed.orgs : [],
          picture: '/assets/icons/user.png',
          version: '?',
          token: keycloakInstance.token
        };

        // Fetch Roles
        keycloakInstance.loadUserInfo().then(userInfo => {
          const roles = userInfo.realm_access.roles;
          setRoles(roles);
          setPrincipal(principalData);
          const org = principalData.orgs.find(org => org === localStorage.getItem(ORG_STORAGE_KEY)) ?? principalData.orgs[0];
          setOrg(org);
        });

      })
      .catch(err => {
        console.error('Keycloak initialization failed:', err);
      });
  }, []);

  /*
  * Hook begins a process to poll keycloak every 20 seconds to see
  * if the token is less than 30 seconds from expiration, in which case
  * it will retrieve a new access token or prompt login
  */
  useEffect(() => {
    if (!principal) return;

    const kc = keycloakRef.current;
    const interval = setInterval(() => {
      kc.updateToken(30).then(refreshed => {
        if (refreshed) {
          console.log('Token was refreshed');
        }
      })
        .catch(err => {
          console.log('Failed to refresh token, forcing login');
          kc.login();
        });
    }, 20_000); // every 20 seconds

    return () => clearInterval(interval);
  }, [principal]);

  const changeOrg = useCallback((newOrg) => {
    localStorage.setItem(ORG_STORAGE_KEY, newOrg);
    setOrg(newOrg);
  }, []);

  // Only render children once the principal is available
  if (!principal) {
    return <></>
  }
  const value = {
    principal,
    org,
    setOrg,
    changeOrg,
    isAuthenticated: () => !!principal,
    getToken: () => (keycloakRef.current ? keycloakRef.current.token : null),
    login: () => {
      if (keycloakRef.current) {
        keycloakRef.current.login();
      }
    },
    logout: () => {
      const redirectUrl = window.location.origin;
      keycloakRef.current.logout({redirectUri: redirectUrl});
    },
    hasRole: (role) => {
      return roles.includes(org + '_' + role);
    },
    getFieldRoleSet: (module, entity, suffix) => {
      const set = new Set();
      const fieldRolePrefix = org + '_' + module?.toUpperCase() + '_' + entity + '__';
      roles.forEach(role => {
        if (role.startsWith(fieldRolePrefix) && role.endsWith('_' + suffix)) {
          const fieldCode = role.replace(fieldRolePrefix, '').replace('_' + suffix, '');
          set.add(fieldCode);
        }
      });
      return set;
    }
  };

  return (
    <PrincipalContext.Provider value={value}>
      {children}
    </PrincipalContext.Provider>
  );
};

export const usePrincipal = () => useContext(PrincipalContext);
